import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WindowContext } from '@blade/os-sdk';
import { useWindowStore, WINDOW_MIN_SIZE, type WindowState } from '../store/windowStore';
import { WindowErrorBoundary } from './ErrorBoundary';

interface Props {
  win: WindowState;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  baseX: number;
  baseY: number;
}

interface ResizeState {
  pointerId: number;
  startX: number;
  startY: number;
  baseW: number;
  baseH: number;
}

export function WindowFrame({ win }: Props) {
  const close = useWindowStore((s) => s.close);
  const minimize = useWindowStore((s) => s.minimize);
  const maximize = useWindowStore((s) => s.maximize);
  const restore = useWindowStore((s) => s.restore);
  const focus = useWindowStore((s) => s.focus);
  const setGeometry = useWindowStore((s) => s.setGeometry);

  const ref = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const [offset, setOffset] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const [sizeDelta, setSizeDelta] = useState<{ dw: number; dh: number }>({ dw: 0, dh: 0 });

  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (win.maximized) return;
      focus(win.id);
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      setDrag({
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseX: win.x,
        baseY: win.y,
      });
    },
    [win.id, win.maximized, win.x, win.y, focus],
  );

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (win.maximized) return;
      e.stopPropagation();
      focus(win.id);
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      setResize({
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseW: win.width,
        baseH: win.height,
      });
    },
    [win.id, win.maximized, win.width, win.height, focus],
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      setOffset({
        dx: e.clientX - drag.startX,
        dy: e.clientY - drag.startY,
      });
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      const finalX = drag.baseX + (e.clientX - drag.startX);
      const finalY = drag.baseY + (e.clientY - drag.startY);
      setDrag(null);
      setOffset({ dx: 0, dy: 0 });
      setGeometry(win.id, { x: finalX, y: finalY });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [drag, setGeometry, win.id]);

  useEffect(() => {
    if (!resize) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== resize.pointerId) return;
      setSizeDelta({
        dw: e.clientX - resize.startX,
        dh: e.clientY - resize.startY,
      });
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== resize.pointerId) return;
      const finalW = Math.max(
        resize.baseW + (e.clientX - resize.startX),
        WINDOW_MIN_SIZE.width,
      );
      const finalH = Math.max(
        resize.baseH + (e.clientY - resize.startY),
        WINDOW_MIN_SIZE.height,
      );
      setResize(null);
      setSizeDelta({ dw: 0, dh: 0 });
      setGeometry(win.id, { width: finalW, height: finalH });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [resize, setGeometry, win.id]);

  const style: React.CSSProperties = win.maximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 44px)',
        zIndex: win.zIndex,
      }
    : {
        transform: `translate3d(${offset.dx}px, ${offset.dy}px, 0)`,
        left: win.x,
        top: win.y,
        width: Math.max(win.width + sizeDelta.dw, WINDOW_MIN_SIZE.width),
        height: Math.max(win.height + sizeDelta.dh, WINDOW_MIN_SIZE.height),
        zIndex: win.zIndex,
      };

  const Body = win.component;
  const ctxValue = useMemo(
    () => ({ windowId: win.id, params: win.params }),
    [win.id, win.params],
  );

  return (
    <div
      ref={ref}
      className="os-window pointer-events-auto absolute flex flex-col overflow-hidden"
      style={style}
      onPointerDownCapture={() => focus(win.id)}
      role="dialog"
      aria-label={win.title}
      data-window-id={win.id}
    >
      <div
        className="os-window-titlebar select-none"
        onPointerDown={onTitlePointerDown}
        onDoubleClick={() =>
          win.maximized ? restore(win.id) : maximize(win.id)
        }
      >
        <span className="os-window-title">{win.title}</span>
        <div className="os-window-controls">
          <button
            type="button"
            aria-label="minimize"
            onClick={(e) => {
              e.stopPropagation();
              minimize(win.id);
            }}
            className="os-window-btn"
          >
            <svg
              className="os-window-icon"
              viewBox="0 0 10 10"
              aria-hidden="true"
            >
              <line x1="1" y1="5" x2="9" y2="5" />
            </svg>
          </button>
          <button
            type="button"
            aria-label={win.maximized ? 'restore' : 'maximize'}
            onClick={(e) => {
              e.stopPropagation();
              win.maximized ? restore(win.id) : maximize(win.id);
            }}
            className="os-window-btn"
          >
            <svg
              className="os-window-icon"
              viewBox="0 0 10 10"
              aria-hidden="true"
            >
              {win.maximized ? (
                <>
                  <rect x="2.5" y="1" width="6.5" height="6.5" rx="0.5" />
                  <rect x="1" y="2.5" width="6.5" height="6.5" rx="0.5" />
                </>
              ) : (
                <rect x="1" y="1" width="8" height="8" rx="0.5" />
              )}
            </svg>
          </button>
          <button
            type="button"
            aria-label="close"
            onClick={(e) => {
              e.stopPropagation();
              close(win.id);
            }}
            className="os-window-btn os-window-btn-close"
          >
            <svg
              className="os-window-icon"
              viewBox="0 0 10 10"
              aria-hidden="true"
            >
              <line x1="1.5" y1="1.5" x2="8.5" y2="8.5" />
              <line x1="8.5" y1="1.5" x2="1.5" y2="8.5" />
            </svg>
          </button>
        </div>
      </div>
      <div className="os-window-body flex-1 overflow-auto">
        <WindowErrorBoundary>
          <WindowContext.Provider value={ctxValue}>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
                  加载中…
                </div>
              }
            >
              <Body {...((win.params ?? {}) as Record<string, unknown>)} />
            </Suspense>
          </WindowContext.Provider>
        </WindowErrorBoundary>
      </div>
      {!win.maximized && (
        <div
          aria-label="resize"
          onPointerDown={onResizePointerDown}
          className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize"
          style={{
            background:
              'linear-gradient(135deg, transparent 50%, var(--color-border) 50%)',
          }}
        />
      )}
    </div>
  );
}
